const DVR_THRESHOLD = 120;
const EVENT_OPTIONS = { passive: false };

/**
 * Wrap a native HTMLMediaElement inside the normalized MediaSurface contract.
 */
class HtmlMediaSurface {
    constructor(media) {
        Object.defineProperty(this, "media", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: media
        });
        Object.defineProperty(this, "element", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.element = media;
    }
    get currentSrc() {
        return this.media.currentSrc || this.media.src || '';
    }
    get currentTime() {
        return this.media.currentTime || 0;
    }
    set currentTime(value) {
        this.media.currentTime = value;
    }
    get duration() {
        return this.media.duration;
    }
    set duration(_value) {
        // Native media duration is read-only.
    }
    get volume() {
        return this.media.volume;
    }
    set volume(value) {
        this.media.volume = value;
    }
    get muted() {
        return this.media.muted;
    }
    set muted(value) {
        this.media.muted = value;
    }
    get playbackRate() {
        return this.media.playbackRate;
    }
    set playbackRate(value) {
        this.media.playbackRate = value;
    }
    get paused() {
        return this.media.paused;
    }
    get ended() {
        return this.media.ended;
    }
    load(source) {
        if (source?.src && this.media.src !== source.src) {
            this.media.src = source.src;
        }
        this.media.load();
    }
    play() {
        return this.media.play();
    }
    pause() {
        this.media.pause();
    }
    on(event, handler, options) {
        const wrapped = ((evt) => {
            if (event === 'error') {
                handler((this.media.error ?? evt));
                return;
            }
            handler(undefined);
        });
        this.media.addEventListener(event, wrapped, options);
        return () => this.media.removeEventListener(event, wrapped, options);
    }
}
function bridgeSurfaceEvents(surface, events) {
    return [
        surface.on('loadstart', () => events.emit('loadstart'), EVENT_OPTIONS),
        surface.on('loadedmetadata', () => events.emit('loadedmetadata'), EVENT_OPTIONS),
        surface.on('durationchange', () => events.emit('durationchange'), EVENT_OPTIONS),
        surface.on('timeupdate', () => events.emit('timeupdate'), EVENT_OPTIONS),
        surface.on('waiting', () => events.emit('waiting'), EVENT_OPTIONS),
        surface.on('seeking', () => events.emit('seeking'), EVENT_OPTIONS),
        surface.on('seeked', () => events.emit('seeked'), EVENT_OPTIONS),
        surface.on('ended', () => events.emit('ended'), EVENT_OPTIONS),
        surface.on('error', (error) => events.emit('error', error), EVENT_OPTIONS),
        surface.on('play', () => events.emit('play'), EVENT_OPTIONS),
        surface.on('playing', () => events.emit('playing'), EVENT_OPTIONS),
        surface.on('pause', () => events.emit('pause'), EVENT_OPTIONS),
        surface.on('volumechange', () => events.emit('volumechange'), EVENT_OPTIONS),
        surface.on('ratechange', () => events.emit('ratechange'), EVENT_OPTIONS),
    ];
}

class BaseMediaEngine {
    constructor() {
        Object.defineProperty(this, "surface", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "events", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "commands", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "surfaceListeners", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
    }
    bindSurfaceEvents(surface, events) {
        this.surface = surface;
        this.events = events;
        this.surfaceListeners = bridgeSurfaceEvents(surface, events);
    }
    unbindSurfaceEvents() {
        this.surfaceListeners.forEach((off) => off());
        this.surfaceListeners = [];
    }
    createMediaSurfaceShim(media) {
        return {
            get currentTime() {
                return media.currentTime;
            },
            set currentTime(v) {
                media.currentTime = v;
            },
            get duration() {
                return media.duration;
            },
            set duration(_v) {
                /* read-only */
            },
            get volume() {
                return media.volume;
            },
            set volume(v) {
                media.volume = v;
            },
            get muted() {
                return media.muted;
            },
            set muted(v) {
                media.muted = v;
            },
            get playbackRate() {
                return media.playbackRate;
            },
            set playbackRate(v) {
                media.playbackRate = v;
            },
            get paused() {
                return media.paused;
            },
            get ended() {
                return media.ended;
            },
            play: () => media.play(),
            pause: () => media.pause(),
            on: (event, handler) => {
                const wrapped = () => handler();
                media.addEventListener(event, wrapped);
                return () => media.removeEventListener(event, wrapped);
            },
        };
    }
    bindMediaEvents(media, events) {
        const shim = this.createMediaSurfaceShim(media);
        this.surfaceListeners = bridgeSurfaceEvents(shim, events);
    }
    unbindMediaEvents() {
        this.unbindSurfaceEvents();
    }
    addMediaListener(media, event, handler, options) {
        media.addEventListener(event, handler, options);
        this.surfaceListeners.push(() => media.removeEventListener(event, handler, options));
    }
    canHandlePlayback(ctx) {
        const owner = ctx.core.leases.owner('playback');
        return !owner || owner === this.name;
    }
    bindCommands(ctx) {
        const { events } = ctx;
        this.commands.push(events.on('cmd:seek', (t) => {
            if (!this.canHandlePlayback(ctx))
                return;
            try {
                ctx.surface.currentTime = t;
            }
            catch {
                // ignore
            }
        }));
        this.commands.push(events.on('cmd:setVolume', (v) => {
            try {
                ctx.surface.volume = v;
            }
            catch {
                // ignore
            }
        }));
        this.commands.push(events.on('cmd:setMuted', (m) => {
            try {
                ctx.surface.muted = m;
            }
            catch {
                // ignore
            }
        }));
        this.commands.push(events.on('cmd:setRate', (r) => {
            if (!this.canHandlePlayback(ctx))
                return;
            try {
                ctx.surface.playbackRate = r;
            }
            catch {
                // ignore
            }
        }));
        this.bindPlayPauseCommands(ctx);
    }
    unbindCommands() {
        for (const off of this.commands)
            off();
        this.commands = [];
    }
    bindPlayPauseCommands(ctx) {
        const { events } = ctx;
        this.commands.push(events.on('cmd:play', () => {
            if (!this.canHandlePlayback(ctx))
                return;
            this.playImpl(ctx.surface);
        }));
        this.commands.push(events.on('cmd:pause', () => {
            if (!this.canHandlePlayback(ctx))
                return;
            this.pauseImpl(ctx.surface);
        }));
    }
    playImpl(surface) {
        const maybePromise = surface.play();
        if (maybePromise && typeof maybePromise.catch === 'function') {
            void maybePromise.catch(() => {
                // ignore
            });
        }
    }
    pauseImpl(surface) {
        surface.pause();
    }
}

class DefaultMediaEngine extends BaseMediaEngine {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'default-engine'
        });
        Object.defineProperty(this, "version", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: '1.0.0'
        });
        Object.defineProperty(this, "capabilities", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ['media-engine']
        });
        Object.defineProperty(this, "priority", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
    }
    canPlay(source) {
        const media = document.createElement('video');
        return media.canPlayType(source.type || '') !== '';
    }
    attach(ctx) {
        const surface = ctx.resetSurface();
        try {
            surface.load?.(ctx.activeSource);
        }
        catch {
            // ignore
        }
        this.bindSurfaceEvents(surface, ctx.events);
        this.bindCommands(ctx);
        this.commands.push(ctx.events.on('cmd:startLoad', () => {
            const s = ctx.core.state.current;
            if (['ready', 'playing', 'paused', 'waiting', 'seeking', 'ended'].includes(s))
                return;
            // ctx.media.preload is intentionally read/written here: it is a DOM attribute
            // with no surface equivalent. The surface abstraction covers playback operations
            // only; loading hints are DOM-level concerns that must be set directly on the element.
            if (ctx.media.preload !== 'none')
                return;
            ctx.media.preload = 'metadata';
            try {
                surface.load?.(ctx.activeSource);
            }
            catch {
                // ignore
            }
        }));
    }
    detach(_ctx) {
        this.unbindCommands();
        this.unbindSurfaceEvents();
    }
}

const defaultConfiguration = {
    startTime: 0,
    duration: 0,
};

class DisposableStore {
    constructor() {
        Object.defineProperty(this, "disposers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "disposed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
    }
    get isDisposed() {
        return this.disposed;
    }
    add(disposer) {
        const d = (disposer ?? (() => { }));
        if (this.disposed) {
            try {
                d();
            }
            catch {
                // best-effort cleanup
            }
            return () => { };
        }
        this.disposers.push(d);
        return d;
    }
    addEventListener(target, type, handler, options) {
        target.addEventListener(type, handler, options);
        return this.add(() => target.removeEventListener(type, handler, options));
    }
    dispose() {
        if (this.disposed)
            return;
        this.disposed = true;
        for (let i = this.disposers.length - 1; i >= 0; i--) {
            try {
                this.disposers[i]();
            }
            catch {
                // best-effort cleanup
            }
        }
        this.disposers = [];
    }
}

class EventBus {
    constructor() {
        Object.defineProperty(this, "listeners", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
    }
    on(event, cb) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(cb);
        return () => this.listeners.get(event).delete(cb);
    }
    emit(event, payload) {
        this.listeners.get(event)?.forEach((cb) => cb(payload));
    }
    listenerCount(event) {
        return this.listeners.get(event)?.size ?? 0;
    }
    clear() {
        this.listeners.clear();
    }
}

class Lease {
    constructor() {
        Object.defineProperty(this, "owners", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "listeners", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
    }
    onChange(cb) {
        this.listeners.push(cb);
        return () => {
            this.listeners = this.listeners.filter((x) => x !== cb);
        };
    }
    notify(capability) {
        const owner = this.owners.get(capability);
        for (const cb of this.listeners) {
            try {
                cb(capability, owner);
            }
            catch {
                // ignore
            }
        }
    }
    acquire(capability, owner) {
        if (this.owners.has(capability))
            return false;
        this.owners.set(capability, owner);
        this.notify(capability);
        return true;
    }
    release(capability, owner) {
        if (this.owners.get(capability) === owner) {
            this.owners.delete(capability);
            this.notify(capability);
        }
    }
    owner(capability) {
        return this.owners.get(capability);
    }
}

class PluginRegistry {
    constructor() {
        Object.defineProperty(this, "plugins", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
    }
    register(plugin) {
        this.plugins.push(plugin);
    }
    all() {
        return this.plugins;
    }
}

class StateManager {
    constructor(state) {
        Object.defineProperty(this, "state", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: state
        });
    }
    get current() {
        return this.state;
    }
    transition(next) {
        this.state = next;
    }
}

function formatTime(seconds, frameRate) {
    const f = Math.floor((seconds % 1) * (frameRate || 0));
    let s = Math.floor(seconds);
    let m = Math.floor(s / 60);
    const h = Math.floor(m / 60);
    const wrap = (value) => {
        const formattedVal = value.toString();
        if (value < 10) {
            if (value <= 0) {
                return '00';
            }
            return `0${formattedVal}`;
        }
        return formattedVal;
    };
    m %= 60;
    s %= 60;
    return `${h > 0 ? `${wrap(h)}:` : ''}${wrap(m)}:${wrap(s)}${f ? `:${wrap(f)}` : ''}`;
}
const generateISODateTime = (d) => {
    const duration = Number.isFinite(d) ? Math.max(0, d) : 0;
    const totalSeconds = Math.floor(duration);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `PT${hours ? `${hours}H` : ''}${minutes ? `${minutes}M` : ''}${seconds}S`;
};
function offset(el) {
    const rect = el.getBoundingClientRect();
    return {
        left: rect.left + (window.pageXOffset || document.documentElement.scrollLeft),
        top: rect.top + (window.pageYOffset || document.documentElement.scrollTop),
    };
}
function isAudio(element) {
    return element instanceof HTMLAudioElement;
}
function isMobile() {
    return ((/ipad|iphone|ipod/i.test(window.navigator.userAgent) && !window.MSStream) ||
        /android/i.test(window.navigator.userAgent));
}
function predictMimeType(media, url) {
    let pathname;
    try {
        pathname = new URL(url).pathname;
    }
    catch {
        // Non-URL string (e.g. a bare video ID): no extension can be determined.
        return isAudio(media) ? 'audio/mp3' : 'video/mp4';
    }
    const fragments = pathname.split('.');
    const extension = fragments.length > 1 ? fragments.pop().toLowerCase() : '';
    // If no extension found, check if media is a vendor iframe
    if (!extension)
        return isAudio(media) ? 'audio/mp3' : 'video/mp4';
    // Check native media types
    switch (extension) {
        case 'm3u8':
        case 'm3u':
            return 'application/x-mpegURL';
        case 'mpd':
            return 'application/dash+xml';
        case 'mp4':
            return isAudio(media) ? 'audio/mp4' : 'video/mp4';
        case 'mp3':
            return 'audio/mp3';
        case 'webm':
            return isAudio(media) ? 'audio/webm' : 'video/webm';
        case 'ogg':
            return isAudio(media) ? 'audio/ogg' : 'video/ogg';
        case 'ogv':
            return 'video/ogg';
        case 'oga':
            return 'audio/ogg';
        case '3gp':
            return 'audio/3gpp';
        case 'wav':
            return 'audio/wav';
        case 'aac':
            return 'audio/aac';
        case 'flac':
            return 'audio/flac';
        default:
            return isAudio(media) ? 'audio/mp3' : 'video/mp4';
    }
}
function readMediaSources(media) {
    const sources = [];
    if (media.src) {
        sources.push({ src: media.src, type: predictMimeType(media, media.src) });
    }
    try {
        media.querySelectorAll('source').forEach((el) => {
            sources.push({ src: el.src, type: el.type || predictMimeType(media, el.src) });
        });
    }
    catch {
        // ignore
    }
    return sources;
}
function resolveMediaEngine(engines, sources) {
    if (sources.length === 0)
        throw new Error('Player cannot resolve media with an empty source');
    for (const source of sources) {
        for (const engine of engines) {
            if (engine.canPlay?.(source)) {
                return { engine, source };
            }
        }
    }
    throw new Error('No compatible media engine found');
}

const clamp01 = (n) => Math.min(1, Math.max(0, n));
class Core {
    constructor(media, config = {}) {
        Object.defineProperty(this, "media", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isLive", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "events", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new EventBus()
        });
        Object.defineProperty(this, "leases", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Lease()
        });
        Object.defineProperty(this, "state", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new StateManager('idle')
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "userInteracted", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "canAutoplay", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "canAutoplayMuted", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "nativeSurface", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "activeSurface", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "interactionUnsubs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "plugins", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new PluginRegistry()
        });
        Object.defineProperty(this, "pluginDisposables", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new WeakMap()
        });
        Object.defineProperty(this, "_src", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_volume", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        Object.defineProperty(this, "_playbackRate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        Object.defineProperty(this, "_currentTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "_muted", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "_duration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "detectedSources", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "activeEngine", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "playerContext", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "autoplaySupport", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "autoplaySupportPromise", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "readyPromise", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "readyResolve", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "readyReject", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "playRequestPromise", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        if (typeof media === 'string') {
            const el = document.querySelector(media);
            if (!el || !(el instanceof HTMLMediaElement)) {
                throw new Error(`OpenPlayer: could not find media element for selector: ${media}`);
            }
            this.media = el;
        }
        else {
            this.media = media;
        }
        this.nativeSurface = new HtmlMediaSurface(this.media);
        this.activeSurface = this.nativeSurface;
        this.registerPlugin(new DefaultMediaEngine());
        this.config = { ...defaultConfiguration, ...config };
        this.media.currentTime = this.config.startTime || this.media.currentTime;
        this._currentTime = this.config.startTime || this.activeSurface.currentTime;
        this._duration = this.config.duration || this.activeSurface.duration;
        const initialVolume = clamp01(this.config.startVolume ?? this.activeSurface.volume);
        this.activeSurface.volume = initialVolume;
        this._volume = initialVolume;
        if (this.config.startVolume !== undefined) {
            this.activeSurface.muted = initialVolume <= 0;
            this._muted = initialVolume <= 0;
        }
        else {
            this._muted = this.activeSurface.muted;
        }
        this.activeSurface.playbackRate = this.config.startPlaybackRate || this.activeSurface.playbackRate;
        this._playbackRate = this.config.startPlaybackRate || this.activeSurface.playbackRate;
        (this.config.plugins || []).forEach((p) => this.registerPlugin(p));
        this.bindStateTransitions();
        this.bindSurfaceSync();
        this.bindLeaseSync();
        this.bindFirstInteraction();
        queueMicrotask(() => this.maybeAutoLoad());
    }
    get surface() {
        return this.activeSurface;
    }
    on(event, cb) {
        // Keep the surface flexible (plugins may emit custom events).
        return this.events.on(event, cb);
    }
    emit(event, payload) {
        this.events.emit(event, payload);
        this.plugins
            .all()
            .filter((p) => !p.capabilities?.includes('media-engine'))
            .forEach((p) => {
            p.onEvent?.(event, payload);
        });
    }
    registerPlugin(plugin) {
        this.plugins.register(plugin);
        const dispose = new DisposableStore();
        this.pluginDisposables.set(plugin, dispose);
        plugin.setup?.({
            core: this,
            media: this.media,
            events: this.events,
            state: this.state,
            leases: this.leases,
            dispose,
            add: (d) => dispose.add(d ?? undefined),
            on: (event, cb) => dispose.add(this.events.on(event, cb)),
            listen: (target, type, handler, options) => dispose.addEventListener(target, type, handler, options),
        });
    }
    set src(value) {
        this._src = value;
        if (value) {
            this.detectedSources = [{ src: value, type: predictMimeType(this.media, value) }];
            this.emit('source:set', value);
            // Tear down any active engine so that load() can re-initialise cleanly
            // with the new source, even when the player is already in a non-idle state.
            if (this.playerContext) {
                this.activeEngine?.detach?.(this.playerContext);
                this.activeEngine = undefined;
                this.playerContext = null;
            }
            this.state.transition('idle');
            this.readyPromise = undefined;
            this.readyResolve = undefined;
            this.readyReject = undefined;
            this.playRequestPromise = undefined;
            queueMicrotask(() => this.load());
        }
    }
    get src() {
        return this._src;
    }
    get volume() {
        return this._volume;
    }
    set volume(v) {
        const next = clamp01(v);
        this._volume = next;
        this.emit('cmd:setVolume', next);
    }
    get muted() {
        return this._muted;
    }
    set muted(muted) {
        this._muted = muted;
        this.emit('cmd:setMuted', muted);
    }
    set playbackRate(rate) {
        this._playbackRate = rate;
        this.emit('cmd:setRate', rate);
    }
    get playbackRate() {
        return this._playbackRate;
    }
    set currentTime(time) {
        this._currentTime = time;
        this.emit('cmd:seek', time);
    }
    get currentTime() {
        return this._currentTime;
    }
    get duration() {
        return this._duration;
    }
    load() {
        if (this.state.current !== 'idle')
            return;
        this.emit('cmd:startLoad');
        this.createReadyPromise();
        const sources = this.detectedSources ?? this.readMediaSources(this.media);
        this.detectedSources = sources;
        const { engine, source: activeSource } = this.resolveMediaEngine(sources);
        this.playerContext = this.createPlayerContext(activeSource);
        this.activeEngine?.detach?.(this.playerContext);
        this.activeEngine = engine;
        this.emit('loadstart');
        this.emit('cmd:load');
        this.activeEngine.attach(this.playerContext);
        this.emit('cmd:setVolume', this._volume);
        this.emit('cmd:setMuted', this._muted);
        this.emit('cmd:setRate', this._playbackRate);
        if (this._currentTime)
            this.emit('cmd:seek', this._currentTime);
    }
    async whenReady() {
        if (this.state.current === 'ready' ||
            this.state.current === 'playing' ||
            this.state.current === 'paused' ||
            this.state.current === 'waiting' ||
            this.state.current === 'seeking' ||
            this.state.current === 'ended') {
            return;
        }
        if (!this.activeEngine)
            this.load();
        this.createReadyPromise();
        return this.readyPromise ?? Promise.resolve();
    }
    async play() {
        if (this.playRequestPromise)
            return this.playRequestPromise;
        if (!this.activeEngine)
            this.load();
        // Emit cmd:play synchronously while the user-gesture task is still active.
        // Browsers (especially Safari) require media.play() to be called in the same
        // microtask/task as the user interaction; any await before this would cause
        // the autoplay policy to reject the play() call on unmuted media.
        this.emit('cmd:play');
        // Await readiness for state-machine consistency (does not re-emit cmd:play).
        this.playRequestPromise = this.whenReady().finally(() => {
            this.playRequestPromise = undefined;
        });
        return this.playRequestPromise;
    }
    async determineAutoplaySupport() {
        if (this.autoplaySupport)
            return this.autoplaySupport;
        if (this.autoplaySupportPromise)
            return this.autoplaySupportPromise;
        // Gate on readiness, but don't fail detection if readiness never arrives.
        await this.whenReady().catch(() => { });
        const media = this.media;
        const defaultVol = media.volume;
        const defaultMuted = media.muted;
        const restore = () => {
            try {
                media.volume = defaultVol;
            }
            catch {
                // ignore
            }
            try {
                media.muted = defaultMuted;
            }
            catch {
                // ignore
            }
            // Keep Player state consistent with the underlying element.
            this._volume = defaultVol;
            this._muted = defaultMuted;
        };
        this.autoplaySupportPromise = (async () => {
            try {
                // Attempt unmuted autoplay first.
                try {
                    const playPromise = media.play();
                    if (playPromise !== undefined)
                        await playPromise;
                    try {
                        media.pause();
                    }
                    catch {
                        // ignore
                    }
                    this.canAutoplay = true;
                    this.canAutoplayMuted = false;
                    return { autoplay: true, muted: false };
                }
                catch {
                    // Unmuted autoplay failed; retry muted autoplay.
                    try {
                        media.volume = 0;
                        media.muted = true;
                        this._volume = 0;
                        this._muted = true;
                    }
                    catch {
                        // ignore
                    }
                    try {
                        const playPromiseMuted = media.play();
                        if (playPromiseMuted !== undefined)
                            await playPromiseMuted;
                        try {
                            media.pause();
                        }
                        catch {
                            // ignore
                        }
                        this.canAutoplay = true;
                        this.canAutoplayMuted = true;
                        return { autoplay: true, muted: true };
                    }
                    catch {
                        // Autoplay is blocked even when muted.
                        this.canAutoplay = false;
                        this.canAutoplayMuted = false;
                        return { autoplay: false, muted: false };
                    }
                }
            }
            finally {
                restore();
            }
        })();
        this.autoplaySupport = await this.autoplaySupportPromise;
        return this.autoplaySupportPromise;
    }
    pause() {
        if (this.state.current === 'idle' || this.state.current === 'loading') {
            return;
        }
        this.emit('cmd:pause');
    }
    destroy() {
        this.events.emit('player:destroy');
        if (this.playerContext)
            this.activeEngine?.detach?.(this.playerContext);
        this.playerContext = null;
        this.plugins.all().forEach((p) => {
            // Always dispose tracked resources first.
            try {
                this.pluginDisposables.get(p)?.dispose();
            }
            catch {
                // ignore
            }
            try {
                p.destroy?.();
            }
            catch {
                // ignore
            }
        });
        this.interactionUnsubs.forEach((u) => u());
        this.interactionUnsubs = [];
        this.events.clear();
    }
    addCaptions(args) {
        const track = document.createElement('track');
        track.kind = args.kind || 'captions';
        track.src = args.src;
        if (args.srclang)
            track.srclang = args.srclang;
        if (args.label)
            track.label = args.label;
        if (args.default)
            track.default = true;
        this.media.appendChild(track);
        this.emit('texttrack:add', track);
        this.emit('texttrack:listchange');
        return track;
    }
    getPlugin(name) {
        return this.plugins.all().find((p) => p?.name === name);
    }
    extend(extension) {
        if (!extension || typeof extension !== 'object')
            return this;
        for (const key of Object.keys(extension)) {
            if (this[key] === undefined) {
                this[key] = extension[key];
            }
            else if (this[key] &&
                typeof this[key] === 'object' &&
                extension[key] &&
                typeof extension[key] === 'object') {
                const target = this[key];
                const source = extension[key];
                for (const k of Object.keys(source)) {
                    if (target[k] === undefined)
                        target[k] = source[k];
                }
            }
        }
        return this;
    }
    bindFirstInteraction() {
        const doc = typeof document !== 'undefined' ? document : null;
        if (!doc)
            return;
        const mark = () => {
            if (this.userInteracted)
                return;
            this.userInteracted = true;
            this.emit('player:interacted');
            this.interactionUnsubs.forEach((u) => u());
            this.interactionUnsubs = [];
        };
        const opts = { capture: true, passive: true };
        const removeOpts = { capture: true };
        const on = (type) => {
            doc.addEventListener(type, mark, opts);
            this.interactionUnsubs.push(() => doc.removeEventListener(type, mark, removeOpts));
        };
        on('pointerdown');
        on('mousedown');
        on('touchstart');
        on('keydown');
    }
    resolveMediaEngine(sources) {
        const engines = this.plugins
            .all()
            .filter((p) => !!(!!p && p.capabilities?.includes('media-engine') && typeof p.canPlay === 'function'));
        // Don't mutate registry order; pass pre-sorted engines to the standalone helper.
        const sortedEngines = [...engines].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0) || a.name.localeCompare(b.name));
        return resolveMediaEngine(sortedEngines, sources);
    }
    bindStateTransitions() {
        // Load lifecycle
        this.events.on('loadstart', () => this.state.transition('loading'));
        // Resolve "ready" when metadata is available (closest HTML5 analogue to previous playback:ready).
        this.events.on('loadedmetadata', () => {
            this.state.transition('ready');
            if (this.readyResolve) {
                this.readyResolve();
                this.readyResolve = undefined;
                this.readyReject = undefined;
            }
        });
        // IMPORTANT: state transitions only on observed playback events (not on commands).
        this.events.on('playing', () => this.state.transition('playing'));
        this.events.on('pause', () => this.state.transition('paused'));
        this.events.on('waiting', () => this.state.transition('waiting'));
        this.events.on('seeking', () => this.state.transition('seeking'));
        this.events.on('seeked', () => this.state.transition('ready'));
        this.events.on('ended', () => this.state.transition('ended'));
        this.events.on('error', (e) => {
            this.state.transition('error');
            if (this.readyReject) {
                this.readyReject(e);
                this.readyResolve = undefined;
                this.readyReject = undefined;
            }
        });
    }
    bindSurfaceSync() {
        const read = () => {
            try {
                this._currentTime = this.activeSurface.currentTime || 0;
            }
            catch {
                // ignore
            }
            try {
                const d = this.config.duration || this.activeSurface.duration;
                this._duration = d;
            }
            catch {
                // ignore
            }
            try {
                this._muted = Boolean(this.activeSurface.muted);
                const v = this.activeSurface.volume;
                if (Number.isFinite(v))
                    this._volume = v;
            }
            catch {
                // ignore
            }
            try {
                const r = this.activeSurface.playbackRate;
                if (Number.isFinite(r))
                    this._playbackRate = r;
            }
            catch {
                // ignore
            }
        };
        this.events.on('loadedmetadata', () => read());
        this.events.on('durationchange', () => read());
        this.events.on('timeupdate', () => read());
        this.events.on('volumechange', () => read());
        this.events.on('ratechange', () => read());
    }
    createPlayerContext(activeSource) {
        const ctx = {
            media: this.media,
            container: (this.media.parentElement ?? this.media),
            events: this.events,
            config: this.config,
            activeSource,
            core: this,
            surface: this.activeSurface, // overridden below with a live getter
            setSurface: (surface) => {
                this.activeSurface = surface;
                return this.activeSurface;
            },
            resetSurface: () => {
                this.activeSurface = this.nativeSurface;
                return this.nativeSurface;
            },
        };
        // Replace the snapshot with a live getter so ctx.surface always reflects the
        // current active surface even after setSurface() is called (e.g. by an iframe engine).
        Object.defineProperty(ctx, 'surface', {
            get: () => this.activeSurface,
            enumerable: true,
            configurable: true,
        });
        return ctx;
    }
    bindLeaseSync() {
        this.leases.onChange((cap) => {
            if (cap !== 'playback')
                return;
            queueMicrotask(() => {
                this.emit('cmd:setVolume', this._volume);
                this.emit('cmd:setMuted', this._muted);
                this.emit('cmd:setRate', this._playbackRate);
                if (this._currentTime)
                    this.emit('cmd:seek', this._currentTime);
            });
        });
    }
    createReadyPromise() {
        if (this.readyPromise)
            return;
        this.readyPromise = new Promise((resolve, reject) => {
            this.readyResolve = resolve;
            this.readyReject = reject;
        });
    }
    readMediaSources(media) {
        return readMediaSources(media);
    }
    maybeAutoLoad() {
        if (this.state.current !== 'idle')
            return;
        const hasEngines = this.plugins.all().some((p) => p.capabilities?.includes('media-engine'));
        if (!hasEngines)
            return;
        const sources = this.readMediaSources(this.media);
        if (sources.length === 0)
            return;
        this.detectedSources = sources;
        // Capture autoplay intent before we clear the src attribute.
        const wantsAutoplay = this.media.autoplay;
        try {
            const sources = this.media.querySelectorAll('source');
            sources.forEach((s) => s.remove());
            if (this.media.getAttribute('src'))
                this.media.removeAttribute('src');
            if (this.media.src)
                this.media.src = '';
            this.load();
            if (wantsAutoplay) {
                // Programmatic media.load() above resets the element and causes some browsers
                // (notably Chromium) not to re-trigger native autoplay after the src is restored.
                // Disable the autoplay attribute to prevent a native-vs-plugin race, then
                // propagate the play intent through Core's event system so plugins (e.g. AdsPlugin)
                // can intercept the preroll before content begins.
                this.media.autoplay = false;
                queueMicrotask(() => this.emit('cmd:play'));
            }
        }
        catch {
            // best effort; don't block attach
        }
    }
}

class OverlayBus {
    constructor(bus) {
        Object.defineProperty(this, "bus", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: bus
        });
    }
    on(event, cb) {
        return this.bus.on(event, cb);
    }
    emit(event, ...data) {
        this.bus.emit(event, ...data);
    }
    clear() {
        this.bus.clear();
    }
}
const OVERLAY_MANAGER_KEY = '__op::overlay::manager';
class OverlayManager {
    constructor() {
        Object.defineProperty(this, "bus", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "active", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "overlays", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        this.bus = new OverlayBus(new EventBus());
    }
    dispose() {
        this.overlays.clear();
        this.active = null;
        this.bus.clear();
    }
    activate(state) {
        this.overlays.set(state.id, state);
        this.recomputeAndEmit();
    }
    update(id, patch) {
        const cur = this.overlays.get(id);
        if (!cur)
            return;
        const next = { ...cur, ...patch, id: cur.id };
        this.overlays.set(id, next);
        this.recomputeAndEmit();
    }
    deactivate(id) {
        const existed = this.overlays.delete(id);
        if (!existed)
            return;
        this.recomputeAndEmit();
    }
    recomputeAndEmit() {
        const next = this.pickActive();
        this.active = next;
        this.bus.emit('overlay:changed', this.active);
    }
    pickActive() {
        let best = null;
        for (const s of this.overlays.values()) {
            if (!best || s.priority > best.priority)
                best = s;
        }
        return best;
    }
}
function getOverlayManager(player) {
    if (player[OVERLAY_MANAGER_KEY])
        return player[OVERLAY_MANAGER_KEY];
    const mgr = new OverlayManager();
    player[OVERLAY_MANAGER_KEY] = mgr;
    try {
        if (player?.events?.on && player?.events?.emit) {
            const off = mgr.bus.on('overlay:changed', (active) => player.events.emit('overlay:changed', active));
            player.events.on('player:destroy', () => {
                try {
                    off();
                }
                catch {
                    // ignore
                }
                try {
                    mgr.dispose();
                }
                catch {
                    // ignore
                }
                try {
                    delete player[OVERLAY_MANAGER_KEY];
                }
                catch {
                    // ignore
                }
            });
        }
    }
    catch {
        // ignore
    }
    return mgr;
}

// ─── IframeMediaSurface ───────────────────────────────────────────────────────
/**
 * A normalized MediaSurface backed by an IframeMediaAdapter.
 *
 * Usage inside an engine's attach():
 *   const surface = new IframeMediaSurface(adapter, { pollIntervalMs: 250 });
 *   ctx.setSurface(surface);
 *   this.bindSurfaceEvents(surface, ctx.events);  // wires internal bus → EventBus
 *   this.bindCommands(ctx);
 *   surface.mount(ctx.container);
 */
class IframeMediaSurface {
    constructor(adapter, options = {}) {
        Object.defineProperty(this, "kind", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'iframe'
        });
        Object.defineProperty(this, "element", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "currentSrc", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "internalBus", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new EventBus()
        });
        Object.defineProperty(this, "adapter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pollIntervalMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pollTimer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // Normalized state — updated by adapter events and polling
        Object.defineProperty(this, "_paused", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "_ended", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "_playIntentAfterEnd", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "_duration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: NaN
        });
        Object.defineProperty(this, "_currentTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "_volume", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        Object.defineProperty(this, "_muted", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "_playbackRate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        this.adapter = adapter;
        this.pollIntervalMs = options.pollIntervalMs ?? 250;
        this.onAdapterReady = this.onAdapterReady.bind(this);
        this.onAdapterState = this.onAdapterState.bind(this);
        this.onAdapterError = this.onAdapterError.bind(this);
        this.adapter.on('ready', this.onAdapterReady);
        this.adapter.on('state', this.onAdapterState);
        this.adapter.on('error', this.onAdapterError);
        // Wire optional push events
        this.adapter.on('timeupdate', ((t) => this.applyTime(t)));
        this.adapter.on('durationchange', ((d) => this.applyDuration(d)));
        this.adapter.on('ratechange', ((r) => this.applyRate(r)));
        this.adapter.on('volumechange', ((v, m) => this.applyVolume(v, m)));
        this.adapter.on('ended', (() => this.applyEnded()));
    }
    // ─── MediaSurface properties (getter + setter adjacent) ──────────────────
    get currentTime() {
        return this._currentTime;
    }
    set currentTime(value) {
        this._currentTime = value;
        this.internalBus.emit('seeking');
        void Promise.resolve(this.adapter.seekTo(value)).then(() => {
            this.internalBus.emit('seeked');
        });
    }
    get duration() {
        return this._duration;
    }
    set duration(_value) {
    }
    get volume() {
        return this._volume;
    }
    set volume(value) {
        const clamped = Math.min(1, Math.max(0, value));
        this.adapter.setVolume(clamped);
        this.applyVolume(clamped, this._muted);
    }
    get muted() {
        return this._muted;
    }
    set muted(value) {
        if (value)
            this.adapter.mute();
        else
            this.adapter.unmute();
        this.applyVolume(this._volume, value);
    }
    get playbackRate() {
        return this._playbackRate;
    }
    set playbackRate(value) {
        this.adapter.setPlaybackRate(value);
        this.applyRate(value);
    }
    get paused() {
        return this._paused;
    }
    get ended() {
        return this._ended;
    }
    // ─── MediaSurface methods ─────────────────────────────────────────────────
    load(_source) {
        // No-op: iframe surfaces are initialized with a fixed source at construction time.
    }
    play() {
        this._playIntentAfterEnd = this._ended;
        return Promise.resolve(this.adapter.play()).then(() => undefined);
    }
    pause() {
        void Promise.resolve(this.adapter.pause());
    }
    /**
     * Subscribe to surface events — satisfies MediaSurface.on() and is used by
     * bridgeSurfaceEvents / BaseMediaEngine.bindSurfaceEvents to forward events
     * onto the shared EventBus.
     */
    on(event, handler, _options) {
        return this.internalBus.on(event, handler);
    }
    // ─── Extra lifecycle (beyond MediaSurface contract) ───────────────────────
    /** Mount the adapter iframe into a DOM container. Resolves once the adapter is ready. */
    async mount(container) {
        await this.adapter.mount(container);
        // Polling starts in onAdapterReady() after the player signals it is initialized.
    }
    destroy() {
        this.stopPolling();
        this.adapter.off('ready', this.onAdapterReady);
        this.adapter.off('state', this.onAdapterState);
        this.adapter.off('error', this.onAdapterError);
        this.adapter.destroy();
        this.internalBus.clear();
    }
    // ─── Adapter → internal bus ───────────────────────────────────────────────
    onAdapterReady() {
        this.applyDuration(this.safeNum(this.adapter.getDuration()));
        this.applyTime(this.safeNum(this.adapter.getCurrentTime()));
        if (this.adapter.getVolume)
            this.applyVolume(this.safeNum(this.adapter.getVolume()), this._muted);
        if (this.adapter.isMuted)
            this.applyVolume(this._volume, !!this.adapter.isMuted());
        if (this.adapter.getPlaybackRate)
            this.applyRate(this.safeNum(this.adapter.getPlaybackRate()));
        if (this.adapter.getElement) {
            this.element = this.adapter.getElement();
        }
        this.startPolling();
        this.internalBus.emit('loadedmetadata');
    }
    onAdapterState(s) {
        switch (s) {
            case 'loading':
            case 'buffering':
                // Don't surface a loading state after the video has ended — the adapter
                // (e.g. YouTube) buffers as it auto-restarts, which would flicker the
                // loader on top of the ended/restart UI.  Only emit 'waiting' once the
                // user has expressed a new play intent.
                if (!this._ended || this._playIntentAfterEnd)
                    this.internalBus.emit('waiting');
                break;
            case 'playing':
                // Some iframe providers fires a spurious PLAYING state immediately after ENDED.
                // Suppress it unless the user explicitly called play() after the video ended.
                if (this._ended && !this._playIntentAfterEnd)
                    break;
                this._paused = false;
                this._ended = false;
                this._playIntentAfterEnd = false;
                this.internalBus.emit('play');
                this.internalBus.emit('playing');
                break;
            case 'paused':
                this._paused = true;
                this.internalBus.emit('pause');
                break;
            case 'ended': {
                // Prevent iframe providers to fire 'ended' spuriously when seekTo() lands within the
                // last ~2 s of the video before playback has actually completed. Only accept 'ended' when
                // the tracked position is close enough to the known duration, or when duration is
                // not yet known (NaN) so we never silently swallow a real end event.
                const ENDED_TOLERANCE_S = 2;
                if (Number.isFinite(this._duration) && this._currentTime < this._duration - ENDED_TOLERANCE_S)
                    break;
                this._paused = true;
                this._ended = true;
                this.internalBus.emit('ended');
                // Explicitly pause the adapter so it doesn't auto-replay (e.g. YouTube
                // restarts from 0 after firing ENDED when seeking near the end).
                void Promise.resolve(this.adapter.pause());
                break;
            }
        }
    }
    onAdapterError(err) {
        this.internalBus.emit('error', err);
    }
    applyTime(t) {
        if (!Number.isFinite(t))
            return;
        this._currentTime = t;
        if (!this._paused && !this._ended)
            this.internalBus.emit('timeupdate');
    }
    applyDuration(d) {
        if (!Number.isFinite(d) || d <= 0)
            return;
        if (d === this._duration)
            return;
        this._duration = d;
        this.internalBus.emit('durationchange');
    }
    applyRate(r) {
        if (!Number.isFinite(r) || r <= 0)
            return;
        if (r === this._playbackRate)
            return;
        this._playbackRate = r;
        this.internalBus.emit('ratechange');
    }
    applyVolume(v, muted) {
        if (!Number.isFinite(v))
            return;
        const clamped = Math.min(1, Math.max(0, v));
        if (clamped === this._volume && !!muted === this._muted)
            return;
        this._volume = clamped;
        this._muted = !!muted;
        this.internalBus.emit('volumechange');
    }
    applyEnded() {
        this._ended = true;
        this._paused = true;
        this.internalBus.emit('ended');
    }
    // ─── Polling ──────────────────────────────────────────────────────────────
    startPolling() {
        if (this.pollTimer != null)
            return;
        const tick = () => {
            this.applyTime(this.safeNum(this.adapter.getCurrentTime()));
            this.applyDuration(this.safeNum(this.adapter.getDuration()));
            if (this.adapter.getVolume || this.adapter.isMuted) {
                const vol = this.adapter.getVolume ? this.safeNum(this.adapter.getVolume()) : this._volume;
                const mut = this.adapter.isMuted ? !!this.adapter.isMuted() : this._muted;
                this.applyVolume(vol, mut);
            }
            if (this.adapter.getPlaybackRate)
                this.applyRate(this.safeNum(this.adapter.getPlaybackRate()));
            this.pollTimer = window.setTimeout(tick, this.pollIntervalMs);
        };
        this.pollTimer = window.setTimeout(tick, this.pollIntervalMs);
    }
    stopPolling() {
        if (this.pollTimer != null) {
            window.clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }
    safeNum(n) {
        const v = Number(n);
        return Number.isFinite(v) ? v : NaN;
    }
}

const CAPTION_KEY = Symbol.for('openplayerjs.caption.provider');
function getCaptionTrackProvider(core) {
    return core[CAPTION_KEY] ?? null;
}
function setCaptionTrackProvider(core, provider) {
    core[CAPTION_KEY] = provider;
}

export { BaseMediaEngine, Core, DVR_THRESHOLD, DefaultMediaEngine, DisposableStore, EVENT_OPTIONS, EventBus, HtmlMediaSurface, IframeMediaSurface, Lease, PluginRegistry, StateManager, formatTime, generateISODateTime, getCaptionTrackProvider, getOverlayManager, isAudio, isMobile, offset, setCaptionTrackProvider };
//# sourceMappingURL=index.js.map
